import React, { useState, useEffect } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Button,
  Select,
  DatePicker,
  Form,
  Alert,
  Divider,
  InputNumber,
} from "antd";
import {
  SwapOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { createTransaction } from '../services/transactionService';
import { getAllAccounts, getAllStocks } from '../services/stockService';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const TransactionForm = ({ onSuccess, onCancel, initialData }) => {
  const [form] = Form.useForm();
  const [accounts, setAccounts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (initialData) {
      // Convert date string to moment object for DatePicker
      const formData = {
        ...initialData,
        transaction_date: initialData.transaction_date ? moment(initialData.transaction_date) : null
      };
      form.setFieldsValue(formData);
    }
  }, [initialData, form]);

  const loadInitialData = async () => {
    try {
      const [accountsData, stocksData] = await Promise.all([
        getAllAccounts(),
        getAllStocks()
      ]);
      setAccounts(accountsData.results || []);
      setStocks(stocksData.results || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load accounts and stocks data');
    }
  };

  const handleStockSelect = (stockId) => {
    const selectedStock = stocks.find(stock => stock.id === parseInt(stockId));
    if (selectedStock) {
      form.setFieldsValue({
        stock_name: selectedStock.stock_name,
        stock_symbol: selectedStock.stock_symbol
      });
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...values,
        transaction_date: values.transaction_date.format('YYYY-MM-DD'),
      };

      await createTransaction(submitData);
      setSuccess('Transaction information has been successfully saved!');
      form.resetFields();

      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      setError(error.response?.data?.error || 'Failed to create transaction record');
    } finally {
      setLoading(false);
    }
  };

  const getTotalAmount = () => {
    const price = form.getFieldValue('price') || 0;
    const quantity = form.getFieldValue('quantity') || 0;
    return (price * quantity).toLocaleString();
  };

  return (
    <Row gutter={[24, 0]} justify="center">
      <Col xs={24} sm={20} md={16} lg={12} xl={10}>
        <Card bordered={false} className="criclebox">
          {/* Header */}
          <div className="number" style={{ marginBottom: '24px' }}>
            <Row align="middle">
              <Col xs={3}>
                <div className="icon-box" style={{ backgroundColor: "#52c41a" }}>
                  <SwapOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
              </Col>
              <Col xs={21}>
                <span style={{ fontSize: '14px', color: '#666' }}>Portfolio Management</span>
                <Title level={3} style={{ margin: 0 }}>
                  Transaction Entry
                </Title>
                <Text style={{ color: '#666' }}>
                  Enter stock buy/sell execution records
                </Text>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Alerts */}
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}
          
          {success && (
            <Alert
              message="Success"
              description={success}
              type="success"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            size="large"
          >
            {/* Transaction Type */}
            <Title level={5} style={{ marginBottom: '12px' }}>Transaction Information</Title>
            
            <Form.Item
              name="transaction_type"
              label="Transaction Type"
              rules={[{ required: true, message: 'Please select transaction type' }]}
            >
              <Select placeholder="Select transaction type" allowClear>
                <Option value="BUY">Buy</Option>
                <Option value="SELL">Sell</Option>
              </Select>
            </Form.Item>

            <Divider />

            {/* Stock Selection */}
            <Title level={5} style={{ marginBottom: '12px' }}>Stock Information</Title>
            
            <Form.Item label="Select from existing stocks">
              <Select
                placeholder="Select a stock"
                onChange={handleStockSelect}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {stocks.map((stock) => (
                  <Option
                    key={stock.id}
                    value={stock.id}
                    label={`${stock.stock_name} (${stock.stock_symbol})`}
                  >
                    {stock.stock_name} ({stock.stock_symbol})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="stock_name"
                  label="Stock Name"
                  rules={[{ required: true, message: 'Please enter stock name' }]}
                >
                  <Input placeholder="e.g., KODEX US Dividend Dow Jones Target Covered Call" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="stock_symbol"
                  label="Stock Symbol"
                >
                  <Input placeholder="e.g., 483290" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Account Selection */}
            <Title level={5} style={{ marginBottom: '12px' }}>Account Information</Title>
            
            <Form.Item label="Select from existing accounts">
              <Select
                placeholder="Select an account"
                onChange={(value) => form.setFieldsValue({ account_name: value })}
                allowClear
              >
                {accounts.map((account) => (
                  <Option key={account.id} value={account.account_name}>
                    {account.account_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="account_name"
              label="Account Name"
              rules={[{ required: true, message: 'Please enter account name' }]}
            >
              <Input placeholder="e.g., 64****26-01" />
            </Form.Item>

            <Divider />

            {/* Transaction Details */}
            <Title level={5} style={{ marginBottom: '12px' }}>Execution Details</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="quantity"
                  label="Execution Quantity (shares)"
                  rules={[
                    { required: true, message: 'Please enter execution quantity' },
                    { type: 'number', min: 1, message: 'Please enter a value of 1 or greater' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="14"
                    min={1}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="price"
                  label="Execution Price (KRW)"
                  rules={[
                    { required: true, message: 'Please enter execution price' },
                    { type: 'number', min: 0.01, message: 'Please enter a value greater than 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="8970"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Total Amount Display */}
            <Form.Item shouldUpdate={(prevValues, currentValues) => 
              prevValues.quantity !== currentValues.quantity || 
              prevValues.price !== currentValues.price
            }>
              {({ getFieldValue }) => {
                const quantity = getFieldValue('quantity') || 0;
                const price = getFieldValue('price') || 0;
                const total = quantity * price;
                
                return total > 0 ? (
                  <Alert
                    message={`Total Execution Amount: ${total.toLocaleString()} KRW`}
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                ) : null;
              }}
            </Form.Item>

            <Form.Item
              name="transaction_date"
              label="Execution Date"
              rules={[{ required: true, message: 'Please select execution date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select execution date"
                format="YYYY-MM-DD"
              />
            </Form.Item>

            <Divider />

            {/* Buttons */}
            <Form.Item style={{ marginBottom: 0 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="default"
                    onClick={onCancel}
                    icon={<CloseOutlined />}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default TransactionForm;
